import { isHttpsUrl } from './block-validation';
import {
  DEFAULT_TEXT_SIZE,
  LEGACY_FONT_SIZE_TO_OPTION,
  SIZE_LOCKED_TAG,
  styleForSize,
} from './rich-text-format';

// SHCT-453 / SHCT-471 / SHCT-473 — normalize rich-text HTML to the canonical
// storage vocabulary the block-editor spec defines (PRD 07 §4.x / 10 §4.5 /
// 11 §4.3: `<strong> <em> <u> <s> <span style="color|font-size"> …`).
//
// Two sources of non-canonical markup have to be folded down to that vocabulary,
// because the backend HTML sanitizer (`HtmlSanitizerService`, allow-list per the
// same spec) silently strips anything outside it — so the formatting survives the
// editor and the preview but disappears after a save→reload round-trip:
//
//   1. `document.execCommand` (toolbar) emits legacy presentational TAGS —
//      `<b>`, `<i>`, `<strike>`, `<font color>`/`<font size>`.   (SHCT-453/471)
//   2. PASTE — the clipboard carries the appearance as inline CSS instead
//      (`font-weight: 700`, `text-decoration: underline`, `color: rgb(…)`), plus
//      whatever junk the source app adds. The backend `style` allow-list permits
//      only `color`, `font-size` and `text-align`, so every other declaration is
//      dropped on save.                                                (SHCT-473)
//
// Both are handled here, on the value that flows out to `onChange` and on pasted
// HTML before it is inserted. It runs on a detached <template>, never on the live
// editor DOM, so the caret and selection are untouched, and it is idempotent:
// content that is already canonical (reloaded-then-resaved) is left as-is.

// SHCT-871 — legacy `<font size="1..7">` used to be mapped to the CSS keyword the browser
// renders it as (`x-small`…`xxx-large`). That keyword scale is exactly what the ticket reports:
// it lands near 10 / 16 / 24 / 48px, so 매우 크게 came out three times 기본. PRD 10 §3.7 now
// names the four px pairs, and this maps the retired buckets onto them so already-saved content
// converts the moment it is re-saved instead of keeping the broken scale forever.

// The backend `style` allow-list (HtmlSanitizerService: CssSchema.withProperties).
// Any other declaration is dropped here so the editor shows what will be stored.
//
// SHCT-871 — `line-height` joins the list because the size spec writes the 행간 alongside the
// 크기 in one span. NOTE: the SERVER allow-list still has to be widened to match (PRD 10 §4.5
// says so explicitly). Until that ships, the size survives a save and the 행간 does not — which
// is a partial render, not a broken one, and the alternative is to keep emitting nothing.
const KEEP_STYLE_PROPS = new Set(['color', 'font-size', 'line-height', 'text-align']);

// The backend element allow-list. Anything else is unwrapped (its text is kept),
// which is what the server does too — mirroring it here keeps the editor honest
// about what a save will preserve.
// SHCT-871 — `h1`/`h3` are gone from the storage vocabulary (PRD 10 §4.5). They are renamed to
// `h2` below rather than dropped, so a heading authored before the change keeps being a heading.
const ALLOWED_TAGS = new Set([
  'p', 'h2', 'blockquote',
  'ul', 'ol', 'li',
  'strong', 'em', 'u', 's', 'br',
  'span', 'div', 'a',
]);

// Elements that carry no visible content — dropped whole, not unwrapped, so a
// Word/Outlook paste doesn't dump its stylesheet into the block as text.
const DROP_WHOLE = 'style, meta, link, script, title';

// Replace `el` with a new element named `tag`, moving all children over.
function rename(el: Element, tag: string): HTMLElement {
  const next = el.ownerDocument.createElement(tag);
  while (el.firstChild) next.appendChild(el.firstChild);
  el.replaceWith(next);
  return next;
}

// Replace `el` with its own children (drops the tag, keeps the content).
function unwrap(el: Element): void {
  el.replaceWith(...Array.from(el.childNodes));
}

function parseStyle(style: string): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const decl of style.split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const value = decl.slice(i + 1).trim();
    if (prop && value) out.push([prop, value]);
  }
  return out;
}

// `font-weight` counts as bold at `bold`/`bolder` or a numeric weight ≥ 600 —
// the CSS definition of the bold range.
function isBoldWeight(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v === 'bold' || v === 'bolder') return true;
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n >= 600;
}

// `rgb()`/`rgba()` → `#rrggbb`. The backend CSS allow-list (OWASP `CssSchema`
// color property) accepts hex / named / keyword values but NOT the functional
// form, so a pasted `color: rgb(220, 38, 38)` would take its whole span with it
// (this is the same constraint SHCT-471 hit from the other direction). Any alpha
// channel is dropped — hex has nowhere to put it and the allow-list has no
// transparent-colour form.
function normalizeColor(value: string): string {
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value.trim());
  if (!m) return value;
  const channels = m.slice(1, 4).map((n) => {
    const v = Math.round(Number.parseFloat(n));
    return Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  });
  return `#${channels.join('')}`;
}

// Fold one element's inline style into the canonical vocabulary: presentational
// declarations become wrapper tags, the three allow-listed properties stay (with
// colors rewritten to hex), everything else is dropped.
function normalizeStyleAttr(el: Element): void {
  const style = el.getAttribute('style');
  if (!style) return;

  let bold = false;
  let italic = false;
  let underline = false;
  let strike = false;
  const kept: string[] = [];

  for (const [prop, value] of parseStyle(style)) {
    if (prop === 'font-weight') {
      if (isBoldWeight(value)) bold = true;
      continue;
    }
    if (prop === 'font-style') {
      const v = value.trim().toLowerCase();
      if (v === 'italic' || v === 'oblique') italic = true;
      continue;
    }
    if (prop === 'text-decoration' || prop === 'text-decoration-line') {
      const v = value.toLowerCase();
      if (v.includes('underline')) underline = true;
      if (v.includes('line-through')) strike = true;
      continue;
    }
    if (KEEP_STYLE_PROPS.has(prop)) {
      kept.push(`${prop}: ${prop === 'color' ? normalizeColor(value) : value}`);
    }
    // anything else: dropped — the backend would strip it anyway
  }

  if (kept.length) el.setAttribute('style', kept.join('; '));
  else el.removeAttribute('style');

  // Wrap the children innermost-first so the nesting reads <strong><em><u><s>.
  for (const [on, tag] of [[strike, 's'], [underline, 'u'], [italic, 'em'], [bold, 'strong']] as const) {
    if (!on) continue;
    const wrapper = el.ownerDocument.createElement(tag);
    while (el.firstChild) wrapper.appendChild(el.firstChild);
    el.appendChild(wrapper);
  }
}

// Remove the noise an external paste (Word, Outlook, Google Docs) carries that
// isn't markup we can map: stylesheets/meta, Office namespaced elements
// (`<o:p>`, `<w:sdt>`), and MSO conditional comments.
function stripPasteJunk(root: DocumentFragment): void {
  root.querySelectorAll(DROP_WHOLE).forEach((el) => el.remove());

  for (const el of Array.from(root.querySelectorAll('*'))) {
    if (el.tagName.includes(':')) unwrap(el);
  }

  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const comments: Node[] = [];
  while (walker.nextNode()) comments.push(walker.currentNode);
  comments.forEach((c) => (c as ChildNode).remove());
}

export interface NormalizeRichHtmlOptions {
  /**
   * SHCT-370 Change 4 — keep only `https://` link hrefs, matching the backend's
   * `HtmlSanitizerService.sanitizeHttpsOnly` (used for 관리지침 · 건강백과 · 상담매뉴얼 block text).
   *
   * OFF by default, and it must stay that way: 공지사항 and 약관 go through the shared
   * `sanitize` policy on the server, which allows `http`/`https`/`mailto`. Turning this on
   * for them would strip links the server would have kept.
   */
  httpsOnlyLinks?: boolean;

  /**
   * SHCT-871 — write 기본(16px) as an explicit span instead of leaving the text bare.
   *
   * PRD 10 §3.7 requires it in as many words: the patient app renders a size it is given and
   * falls back to **14px** when it is given none, so text the author never resized comes out a
   * step smaller than the 기본 the editor showed them. Relying on the author to pick 기본 does
   * not close this — 기본 is the option they had no reason to touch.
   *
   * OFF by default, and it must stay that way: §3.7 governs the three block editors
   * (관리지침 · 건강백과 · 상담매뉴얼) that share one toolbar. 공지사항 and 약관 have no size
   * control at all, and stamping a size onto their bodies would invent formatting the author
   * never asked for.
   */
  explicitDefaultSize?: boolean;
}

/**
 * True when `html` carries at least one link the `httpsOnlyLinks` normalization would drop.
 *
 * Callers use this to TELL the author instead of silently deleting a link they pasted —
 * PRD 07 §3 line 129 says such a link is 거부, and a rejection nobody sees is not a rejection.
 */
export function hasNonHttpsLink(html: string): boolean {
  if (!html) return false;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  return Array.from(tpl.content.querySelectorAll('a[href]')).some(
    (a) => !isHttpsUrl(a.getAttribute('href')),
  );
}

/**
 * Give every run of text a size, so none of it reaches the patient app unsized (SHCT-871).
 *
 * Runs LAST, after the allow-list sweep and the empty-span unwrap, so the spans it adds are not
 * swept away again and it never has to reason about tags that will not survive.
 *
 * Two exemptions, both from PRD 10 §3.7. Text already under a span that declares a `font-size`
 * keeps that size — the author chose it. Text inside a 소제목 is left alone because §3.7 fixes
 * the heading's size and the size dropdown is disabled there; writing a 본문 size into it would
 * put a value on the page that the toolbar has no way to show or change.
 */
function applyDefaultSize(root: DocumentFragment): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const bare: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    // Whitespace between block tags is layout, not content — wrapping it would litter the
    // stored HTML with spans that render nothing.
    if (!text.data.trim()) continue;
    let sized = false;
    for (let el = text.parentElement; el && el !== (root as unknown as Element); el = el.parentElement) {
      const tag = el.tagName.toLowerCase();
      if (tag === SIZE_LOCKED_TAG || el.style?.fontSize) {
        sized = true;
        break;
      }
    }
    if (!sized) bare.push(text);
  }
  for (const text of bare) {
    const span = document.createElement('span');
    span.setAttribute('style', styleForSize(DEFAULT_TEXT_SIZE));
    text.replaceWith(span);
    span.appendChild(text);
  }
}

export function normalizeRichHtml(html: string, options: NormalizeRichHtmlOptions = {}): string {
  if (!html) return html;

  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const root = tpl.content;

  stripPasteJunk(root);

  // Google Docs wraps its whole clipboard payload in
  // `<b style="font-weight:normal" id="docs-internal-guid-…">`. Renaming that to
  // <strong> below would bold the entire paste, so drop the wrapper first —
  // any <b>/<strong> that explicitly declares a non-bold weight is not bold.
  root.querySelectorAll('b[style], strong[style]').forEach((el) => {
    const weight = parseStyle(el.getAttribute('style') ?? '').find(([p]) => p === 'font-weight');
    if (weight && !isBoldWeight(weight[1])) unwrap(el);
  });

  // SHCT-871 — 문단 2종: every heading level collapses to 소제목. Runs before the allow-list
  // sweep so an existing `<h1>`/`<h3>` becomes a subheading instead of being unwrapped to bare
  // text, which would lose the author's structure.
  root.querySelectorAll('h1, h3, h4, h5, h6').forEach((el) => rename(el, 'h2'));

  // Inline text tags → canonical equivalents.
  root.querySelectorAll('b').forEach((el) => rename(el, 'strong'));
  root.querySelectorAll('i').forEach((el) => rename(el, 'em'));
  root.querySelectorAll('strike').forEach((el) => rename(el, 's'));

  // `<font color=…/size=…>` → `<span style="color:…;font-size:…">` (only the
  // color/font-size the allow-list permits; face and unknown attrs are dropped).
  //
  // SHCT-471 — the style string is written by hand, NOT via `span.style.color`.
  // Assigning through the CSSOM re-serializes a hex color (`#dc2626`) to the
  // `rgb(220, 38, 38)` functional form, which the backend sanitizer's CSS
  // allow-list (OWASP `CssSchema` color property) does NOT accept — it permits
  // hex / named / keyword values only. A rejected value makes the server strip
  // the whole span on save, so the authored color silently vanished after a
  // save→reload round-trip. Writing the attribute directly keeps the original
  // hex (which the browser already emits in `<font color="#…">`) so it survives.
  root.querySelectorAll('font').forEach((el) => {
    const color = el.getAttribute('color');
    const size = el.getAttribute('size');
    const span = rename(el, 'span');
    const decls: string[] = [];
    if (color) decls.push(`color: ${color}`);
    const mapped = size ? LEGACY_FONT_SIZE_TO_OPTION[size] : undefined;
    if (mapped) decls.push(styleForSize(mapped));
    if (decls.length) span.setAttribute('style', decls.join('; '));
  });

  // SHCT-473 — fold inline styles (the shape a paste arrives in) into the
  // canonical tags + the three allow-listed properties.
  root.querySelectorAll('[style]').forEach(normalizeStyleAttr);

  // Drop tags the backend doesn't allow, keeping their text (same outcome the
  // server produces, just visible in the editor now).
  for (const el of Array.from(root.querySelectorAll('*'))) {
    if (!ALLOWED_TAGS.has(el.tagName.toLowerCase())) unwrap(el);
  }

  // Only `style` (any element) and `href` (links) survive the backend's
  // attribute allow-list — strip the rest so pasted class/id/lang junk doesn't
  // linger in the editor DOM.
  for (const el of Array.from(root.querySelectorAll('*'))) {
    const tag = el.tagName.toLowerCase();
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name === 'style') continue;
      if (name === 'href' && tag === 'a') {
        // SHCT-370 Change 4 — an href the server will refuse is dropped HERE, so the editor and the
        // preview stop showing a link that a save is about to delete. The toolbar's link button
        // already blocks a non-https URL, but a PASTE carries its own hrefs straight past it: the
        // author saw a working link, saved, and the backend's https-only policy removed it with
        // nothing on screen to say so.
        if (options.httpsOnlyLinks && !isHttpsUrl(attr.value)) el.removeAttribute(attr.name);
        continue;
      }
      el.removeAttribute(attr.name);
    }
  }

  // An `<a>` with no href is no longer a link — unwrap it so what stays is the plain text the
  // server would store. (The server keeps the empty tag instead; the rendered result is the same,
  // and leaving a linkless `<a>` in the editor would let a later paste of an href re-arm it.)
  root.querySelectorAll('a:not([href])').forEach(unwrap);

  // A <span> left with no attributes carries no meaning — unwrap it.
  root.querySelectorAll('span').forEach((el) => {
    if (el.attributes.length === 0) unwrap(el);
  });

  if (options.explicitDefaultSize) applyDefaultSize(root);

  return tpl.innerHTML;
}
